import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { COMPANY_COLORS } from '@/constants/colors.constant'
import {
  useAttributionDailySummaries,
  useAttributionDateRange,
  useAttributionPeriodLabel,
  useAttributionTotalReturns,
} from '@/contexts/FAAttributionDataContext/hooks'
import { useIsAnalysisLoading } from '@/contexts/RADashboardDataContext/hooks'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { CardProps } from '@/interfaces/raDashboard.interface'
import { formatReturnValue } from '@/utils/attributionReturnFormat'
import { clampSummariesToRange } from '@/utils/dailyReturnUtils'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useMemo } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

const POSITIVE_COLOR = COMPANY_COLORS.SUCCESS
const NEGATIVE_COLOR = COMPANY_COLORS.ERROR

// Active Return card: the aggregate value (totalReturn API) with an out/under-performance note, and
// a green/red column sparkline of the daily active return series (summary API 'd' field).
const ActiveReturnChartCard: React.FC<CardProps> = ({ cardId }) => {
  const theme = useTheme()
  const isLoading = useIsAnalysisLoading()
  const totalReturn = useAttributionTotalReturns()
  const dailySummaries = useAttributionDailySummaries()
  const dateRange = useAttributionDateRange()
  const periodLabel = useAttributionPeriodLabel()
  const title = CardTitleLookup[cardId]

  const value = totalReturn == null ? null : totalReturn.differenceContribution
  const { text: valueText, sign } = formatReturnValue(value, isLoading)
  const valueColor = sign === 'none' ? theme.textSecondary : sign === 'positive' ? POSITIVE_COLOR : NEGATIVE_COLOR
  const description =
    value == null ? undefined : value >= 0 ? 'Outperformance vs benchmark' : 'Underperformance vs benchmark'

  // Daily active return ('d') series, clamped to the analyzed range.
  const series = useMemo(
    () => clampSummariesToRange(dailySummaries, dateRange).map((p) => p.active),
    [dailySummaries, dateRange],
  )
  // A single column still renders (unlike a one-point spline), so show the chart from one point on.
  const hasChart = series.length >= 1

  // Green bars for outperformance days, red for underperformance (split at zero).
  const chartOptions = useMemo(
    (): Highcharts.Options => ({
      chart: { type: 'column', backgroundColor: 'transparent', margin: [4, 0, 4, 0], animation: false },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: { visible: false },
      yAxis: { visible: false },
      tooltip: { enabled: false },
      plotOptions: {
        column: {
          color: POSITIVE_COLOR,
          negativeColor: NEGATIVE_COLOR,
          threshold: 0,
          borderWidth: 0,
          pointPadding: 0.05,
          groupPadding: 0,
          states: { hover: { enabled: false } },
        },
      },
      series: [{ type: 'column', data: series }],
    }),
    [series],
  )

  return (
    <Stack
      sx={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        padding: '12px',
        boxSizing: 'border-box',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack sx={{ borderBottom: `1px solid ${theme.border}` }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: theme.textPrimary,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
      </Stack>
      {periodLabel && (
        <Typography sx={{ fontSize: '12px', color: theme.textSecondary, mt: '4px' }}>
          {periodLabel}
        </Typography>
      )}
      <Stack direction='row' sx={{ flex: 1, minHeight: 0, alignItems: 'center', gap: '12px' }}>
        <Stack sx={{ flexShrink: 0, justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: valueColor }}>{valueText}</Typography>
        </Stack>
        {hasChart && (
          <Stack sx={{ flex: 1, minWidth: 0, alignSelf: 'stretch' }}>
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              containerProps={{ style: { height: '100%', width: '100%' } }}
            />
          </Stack>
        )}
      </Stack>
      {description && (
        <Typography sx={{ fontSize: '13px', color: theme.textSecondary, mt: '4px' }}>{description}</Typography>
      )}
    </Stack>
  )
}

export default ActiveReturnChartCard
